import {
  Box,
  Wrap,
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useTotalCollectiveRisk,
  useTotalPopulation,
  useTotalVKTPersonalRisk,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Network DSI',
  content: [
    {
      title: 'Total per 100,000 population',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population. The Totals may include crashes outside the road network, e.g. off-road or on private roads, or on State Highways for Territorial Authorities.
  </Text>,
      source: null,
    },
    {
      title: 'Total per 100 Million VKT (personal risk)',
      description:
  <Text fontSize="inherit" mb={2}>
    Personal Risk is a measure of the danger to an individual. Personal Risk is the crash rate measured as the deaths and serious injuries (DSI) per 100 million vehicle kilometres travelled (VKT) in the selected network area. The Totals may include crashes outside the road network, e.g. off-road or on private roads, or on State Highways for Territorial Authorities.
  </Text>,
      source: null,
    },
    {
      title: 'Total per 1,000 km (network collective risk)',
      description:
  <Text fontSize="inherit" mb={2}>
    Collective Risk is a measure of the road network safety. Collective Risk is the crash density measured as the total number of deaths and serious injuries (DSI) per 1,000 kilometres each year in the selected network area. The Totals may include crashes outside the road network, e.g. off-road or on private roads, or on State Highways for Territorial Authorities.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Crash data sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/partners/data-and-tools/crash-analysis-system" isExternal>NZTA Crash Analysis System.</Link>
    {' '}
    Population sourced from Statistics New Zealand
    {' '}
    <Link href="https://www.stats.govt.nz/information-releases/subnational-population-estimates-at-30-june-2023/" isExternal>Subnational Population Estimates.</Link>
    {' '}
    Network length and Vehicle Kilometres Travelled sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools.</Link>
    {' '}
    Road Efficiency Group Te Ringa Maimoa has calculated these results from these data sources.
  </Text>,
    },
  ],
};

function RoadSafetyNetwork() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: totalPopulation, isLoading: isLoadingTotalPopulation } = useTotalPopulation();
  const { data: totalVKTPersonalRisk, isLoading: isLoadingTotalVKTPersonalRisk } = useTotalVKTPersonalRisk();
  const { data: totalCollectiveRisk, isLoading: isLoadingTotalCollectiveRisk } = useTotalCollectiveRisk();
  const isLoading = isLoadingTotalPopulation || isLoadingTotalVKTPersonalRisk || isLoadingTotalCollectiveRisk;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Check if all lockYears are the same
  const allLockYears = [
    totalPopulation?.[0]?.LockYear,
    totalVKTPersonalRisk?.[0]?.LockYear,
    totalCollectiveRisk?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // Total Population Chart
  const totalPopulationYears = [...new Set(totalPopulation?.map((item) => item.Year))];
  const totalPopulationData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: totalPopulation?.map((item) => ({ x: item.Year, y: Math.round(item.Value) })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: totalPopulation?.map((item) => ({ x: item.Year, y: Math.round(item.PeerGroupValue) })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: totalPopulation?.map((item) => ({ x: item.Year, y: [Math.round(item.LowPercentile), Math.round(item.HighPercentile)] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const totalPopulationChartSeries = filterChartDataByName(
    totalPopulationData,
    chartFilters,
  );

  // Total VKT Personal Risk Chart
  const totalVKTPersonalRiskYears = [...new Set(totalVKTPersonalRisk?.map((item) => item.Year))];
  const totalVKTPersonalRiskData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: totalVKTPersonalRisk?.map((item) => ({ x: item.Year, y: Math.round(item.Value) })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: totalVKTPersonalRisk?.map((item) => ({ x: item.Year, y: Math.round(item.PeerGroupValue) })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: totalVKTPersonalRisk?.map((item) => ({ x: item.Year, y: [Math.round(item.LowPercentile), Math.round(item.HighPercentile)] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const totalVKTPersonalRiskChartSeries = filterChartDataByName(
    totalVKTPersonalRiskData,
    chartFilters,
  );

  // Total Collective Risk Chart
  const totalCollectiveRiskYears = [...new Set(totalCollectiveRisk?.map((item) => item.Year))];
  const totalCollectiveRiskData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: totalCollectiveRisk?.map((item) => ({ x: item.Year, y: Math.round(item.Value) })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: totalCollectiveRisk?.map((item) => ({ x: item.Year, y: Math.round(item.PeerGroupValue) })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: totalCollectiveRisk?.map((item) => ({ x: item.Year, y: [Math.round(item.LowPercentile), Math.round(item.HighPercentile)] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const totalCollectiveRiskChartSeries = filterChartDataByName(
    totalCollectiveRiskData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Network DSI"
      informationSource={informationSource}
      links={[
        {
          label: 'ONF Safe Travel - Personal/Collective Risk',
          url: '/onf/transport-outcomes/hsp2',
          isExternal: false,
          userRoles: null,
        },
      ]}
      isLoading={isLoading || !totalPopulation || !totalVKTPersonalRisk || !totalCollectiveRisk}
      lockYear={primaryLockYear || null}
    >
      <Wrap spacing={0}>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={totalPopulation?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="Total per 100,000 population"
            categories={totalPopulationYears}
            series={totalPopulationChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={0}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={totalVKTPersonalRisk?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="Total per 100M VKT (personal risk)"
            categories={totalVKTPersonalRiskYears}
            series={totalVKTPersonalRiskChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={0}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={totalCollectiveRisk?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title="Total per 1,000 km (collective risk)"
            categories={totalCollectiveRiskYears}
            series={totalCollectiveRiskChartSeries}
            width="240"
            height="280"
            stroke={stroke}
            markers={markers}
            prefix=""
            suffix=""
            decimalPoints={0}
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default RoadSafetyNetwork;
